import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  MenuItem,
  Card,
  Chip,
  Stack,
  Collapse,
} from "@mui/material";
import { useAuthContext } from "../context/AuthContext";
import { fetchData, testPassagensConnection } from "../api/client";
import {
  findIntegrationCardByKey,
  INTEGRATION_CARDS,
  INTEGRATION_TYPES,
  inferIntegrationTypeKey,
} from "../constants/integrationCatalog";
import {
  passagensByKeyFromConfigurationsResponse,
  createEmptyPassagensForm,
  mergePassagensFormFromByKey,
  buildPassagensSavePairs,
  passagensUiSplitFields,
} from "../constants/passagensConfigHelpers";

const digitsOnly = (s) => String(s ?? "").replace(/[^\d]/g, "");
const n = (v) => (v === "" || v == null ? null : Number(v));

const AUTH_TYPES = [
  { label: "API Key", value: "API_KEY" },
  { label: "OAuth2", value: "OAUTH2" },
  { label: "Basic", value: "BASIC" },
  { label: "HMAC", value: "HMAC" },
];

function TypeCard({ card, selected, onClick }) {
  const Icon = card.icon;
  return (
    <Card
      onClick={onClick}
      role="button"
      aria-pressed={selected}
      sx={(theme) => ({
        position: "relative",
        p: 2.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: "pointer",
        transition: "box-shadow .2s ease, transform .05s ease",
        "&:hover": { boxShadow: 4 },
        boxShadow: selected ? `inset 0 0 0 2px ${theme.palette.primary.main}` : "none",
        zIndex: selected ? 2 : 1,
        minHeight: 100,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      })}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Icon />
        <Typography variant="subtitle1" fontWeight={700}>
          {card.label}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {card.description}
      </Typography>
      {selected && (
        <Box mt="auto">
          <Chip size="small" label="Selecionado" color="primary" />
        </Box>
      )}
    </Card>
  );
}

const AddIntegrationForm = ({ data, onClose, onSubmit, isLoading, onTest, onPassagensSaved }) => {
  const { userData } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);

  const isEditing = Boolean(data?.id);
  const initialTypeKey = useMemo(() => inferIntegrationTypeKey(data), [data]);
  const [step, setStep] = useState(isEditing ? 2 : 1);
  const [typeKey, setTypeKey] = useState(initialTypeKey || INTEGRATION_TYPES.DEFAULT);

  const [form, setForm] = useState({
    ...data,
    status: data?.status == null ? 1 : Number(data.status),
    ordem: data?.ordem ?? "",
    id_usuario: data?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,

    nome: data?.nome || "",
    provedor: data?.provedor || "",
    tipo_auth: data?.tipo_auth || "API_KEY",
    base_url: data?.base_url || "",
    scopes: Array.isArray(data?.scopes) ? data.scopes.join(",") : (data?.scopes || ""),
    observacoes: data?.observacoes || "",

    // credenciais (obj no payload)
    api_key: "",
    client_id: data?.credentials?.client_id || "",
    client_secret: "",
    username: data?.credentials?.username || "",
    password: "",
    webhook_secret: "",
    webhook_url: data?.webhook_url || "",

    // flags locais para saber se já existe segredo configurado (quando backend ainda não expõe has_*)
    has_api_key: !!data?.credentials?.api_key,
    has_client_secret: !!data?.credentials?.client_secret,
    has_password: !!data?.credentials?.password,
    has_webhook_secret: !!data?.credentials?.webhook_secret,
  });

  useEffect(() => {
    const inferred = inferIntegrationTypeKey(data);
    setTypeKey(inferred || INTEGRATION_TYPES.DEFAULT);
    if (Boolean(data?.id)) setStep(2);
    setForm((prev) => ({
      ...prev,
      ...data,
      status: data?.status == null ? 1 : Number(data.status),
      ordem: data?.ordem ?? "",
      id_usuario: data?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,

      nome: data?.nome || "",
      provedor: data?.provedor || "",
      tipo_auth: data?.tipo_auth || "API_KEY",
      base_url: data?.base_url || "",
      scopes: Array.isArray(data?.scopes) ? data.scopes.join(",") : (data?.scopes || ""),
      observacoes: data?.observacoes || "",

      api_key: "",
      client_id: data?.credentials?.client_id || "",
      client_secret: "",
      username: data?.credentials?.username || "",
      password: "",
      webhook_secret: "",
      webhook_url: data?.webhook_url || "",

      has_api_key: !!data?.credentials?.api_key,
      has_client_secret: !!data?.credentials?.client_secret,
      has_password: !!data?.credentials?.password,
      has_webhook_secret: !!data?.credentials?.webhook_secret,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  // defaults ao escolher tipo (apenas em inclusão)
  useEffect(() => {
    if (isEditing) return;
    const card = findIntegrationCardByKey(typeKey);
    setForm((prev) => {
      const next = { ...prev };
      if ((!next.provedor || String(next.provedor).trim() === "") && card.providerHint) {
        next.provedor = card.providerHint;
      }
      if ((!next.nome || String(next.nome).trim() === "") && card.label) {
        next.nome = card.label;
      }
      // webhooks geralmente precisam de callback
      if (typeKey === INTEGRATION_TYPES.WEBHOOK && !next.webhook_url) {
        next.webhook_url = next.webhook_url || "";
      }
      return next;
    });
  }, [typeKey, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const maskSecretField = (field) => {
    setForm((prev) => ({
      ...prev,
      [field]: "",
      [`has_${field}`]: false,
    }));
  };

  const buildPayload = () => {
    const credentials = {
      api_key: form.api_key?.trim() || undefined,
      client_id: form.client_id?.trim() || undefined,
      client_secret: form.client_secret?.trim() || undefined,
      username: form.username?.trim() || undefined,
      password: form.password?.trim() || undefined,
      webhook_secret: form.webhook_secret?.trim() || undefined,
    };

    // remove undefined
    Object.keys(credentials).forEach((k) => credentials[k] === undefined && delete credentials[k]);

    return {
      id: form.id,
      nome: (form.nome || "").trim(),
      provedor: (form.provedor || "").trim(),
      tipo_auth: form.tipo_auth,
      base_url: (form.base_url || "").trim(),
      scopes: (form.scopes || "").split(",").map((s) => s.trim()).filter(Boolean),
      observacoes: form.observacoes || "",
      ordem: n(digitsOnly(form.ordem)),
      status: Number(form.status) === 1 ? 1 : 0,
      id_usuario: form.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      credentials,
      webhook_url: (form.webhook_url || "").trim() || undefined,
    };
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      if (!payload.nome) throw new Error("Nome é obrigatório.");
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Erro ao salvar a integração.");
    }
  };

  const testNow = async () => {
    setErrorMessage(null);
    try {
      const payload = buildPayload();
      await onTest?.(payload.id ? { id: payload.id } : payload);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || err?.message || "Falha ao testar a integração.");
    }
  };

  const showApiKey = form.tipo_auth === "API_KEY" || form.tipo_auth === "HMAC";
  const showOAuth = form.tipo_auth === "OAUTH2";
  const showBasic = form.tipo_auth === "BASIC";

  const isPassagensType = String(typeKey || "").startsWith("passagens_");
  const passagensPartner = useMemo(() => {
    const card = findIntegrationCardByKey(typeKey);
    return card?.passagensPartner || null;
  }, [typeKey]);

  const passagensTypeCard = useMemo(() => findIntegrationCardByKey(typeKey), [typeKey]);
  const passagensTestUiLabel = useMemo(() => {
    if (!passagensTypeCard || !passagensPartner) return "";
    if (passagensPartner === "totalbus") return "RJ (Totalbus)";
    return passagensTypeCard.label.replace(/^Passagens - /, "");
  }, [passagensTypeCard, passagensPartner]);

  const passagensFieldSplit = useMemo(
    () => passagensUiSplitFields(passagensPartner),
    [passagensPartner]
  );

  const [passagensForm, setPassagensForm] = useState(createEmptyPassagensForm);
  const [passagensLoading, setPassagensLoading] = useState(false);
  const [passagensSaving, setPassagensSaving] = useState(false);
  const [passagensTesting, setPassagensTesting] = useState("");
  const [passagensMessage, setPassagensMessage] = useState(null);
  const [passagensTestStatus, setPassagensTestStatus] = useState({});
  const [passagensShowAdvanced, setPassagensShowAdvanced] = useState(false);

  useEffect(() => {
    setPassagensShowAdvanced(false);
  }, [passagensPartner]);

  useEffect(() => {
    if (!isPassagensType) return;
    const loadPassagensConfig = async () => {
      try {
        setPassagensLoading(true);
        const res = await fetchData("/configurations", "GET");
        const byKey = passagensByKeyFromConfigurationsResponse(res);
        setPassagensForm(() => mergePassagensFormFromByKey(byKey));
      } catch (err) {
        setPassagensMessage({
          type: "error",
          text: err?.response?.data?.message || "Erro ao carregar configurações de passagens.",
        });
      } finally {
        setPassagensLoading(false);
      }
    };
    loadPassagensConfig();
  }, [isPassagensType]);

  useEffect(() => {
    if (!isPassagensType) return;
    const company =
      (typeof window !== "undefined" ? localStorage.getItem("company") : "") ||
      userData?.company ||
      "default";
    const key = `passagens-test-status:${company}`;
    try {
      const raw = localStorage.getItem(key);
      setPassagensTestStatus(raw ? JSON.parse(raw) : {});
    } catch (_) {
      setPassagensTestStatus({});
    }
  }, [isPassagensType, userData?.company]);

  const savePassagensConfig = async () => {
    try {
      setPassagensSaving(true);
      setPassagensMessage(null);
      const updates = buildPassagensSavePairs(passagensPartner, passagensForm);
      if (!updates.length) throw new Error("Tipo de passagens inválido.");
      await Promise.all(
        updates.map(([key, value]) => fetchData(`/configurations/${key}`, "PUT", { value }))
      );
      setPassagensMessage({
        type: "success",
        text: "Configurações de passagens salvas para este workspace.",
      });
      onPassagensSaved?.();
    } catch (err) {
      setPassagensMessage({
        type: "error",
        text: err?.response?.data?.message || "Erro ao salvar configurações de passagens.",
      });
    } finally {
      setPassagensSaving(false);
    }
  };

  const runPassagensTest = async (target) => {
    const company =
      (typeof window !== "undefined" ? localStorage.getItem("company") : "") ||
      userData?.company ||
      "default";
    const storageKey = `passagens-test-status:${company}`;
    try {
      setPassagensTesting(target);
      setPassagensMessage(null);
      const res = await testPassagensConnection(target);
      const next = {
        ...passagensTestStatus,
        [target]: {
          ok: true,
          message: res?.message || "Conexão OK.",
          at: new Date().toISOString(),
        },
      };
      setPassagensTestStatus(next);
      localStorage.setItem(storageKey, JSON.stringify(next));
      const testLabel =
        INTEGRATION_CARDS.find((c) => c.passagensPartner === target)?.label?.replace(/^Passagens - /, "") ||
        target;
      setPassagensMessage({ type: "success", text: `${testLabel}: ${res?.message || "Conexão OK."}` });
    } catch (err) {
      const next = {
        ...passagensTestStatus,
        [target]: {
          ok: false,
          message: err?.message || err?.response?.data?.message || `Falha no teste de ${target}.`,
          at: new Date().toISOString(),
        },
      };
      setPassagensTestStatus(next);
      localStorage.setItem(storageKey, JSON.stringify(next));
      setPassagensMessage({
        type: "error",
        text: err?.message || err?.response?.data?.message || `Falha no teste de ${target}.`,
      });
    } finally {
      setPassagensTesting("");
    }
  };

  const statusChip = (target) => {
    const st = passagensTestStatus?.[target];
    if (!st) return <Chip size="small" label="Não testado" variant="outlined" />;
    const date = st.at ? new Date(st.at).toLocaleString() : "";
    return (
      <Chip
        size="small"
        color={st.ok ? "success" : "error"}
        label={st.ok ? `OK ${date}` : `Falha ${date}`}
        title={st.message || ""}
      />
    );
  };

  const visible = useMemo(() => {
    // default: mostra tudo
    const base = {
      authType: true,
      provider: true,
      baseUrl: true,
      order: true,
      status: true,
      credentials: true,
      webhook: true,
      scopes: true,
      observations: true,
      testButton: true,
      saveButton: true,
    };

    if (isPassagensType) return base;

    if (typeKey === INTEGRATION_TYPES.WEBHOOK) {
      return {
        ...base,
        baseUrl: false,
        scopes: false,
        // credenciais continuam dependendo do auth type; mas por padrão webhooks usam secret+url
        credentials: false,
        testButton: false,
      };
    }

    if (typeKey === INTEGRATION_TYPES.POWER_BI) {
      return {
        ...base,
        webhook: false,
      };
    }

    return base;
  }, [isPassagensType, typeKey]);

  return (
    <Paper elevation={3} sx={{ padding: 3, width: "95%", margin: "0 auto" }}>
      <Typography variant="h6" gutterBottom>
        {form?.id ? "Editar Integração" : "Adicionar Integração"}
      </Typography>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {!isEditing && step === 1 ? (
        <>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Selecione o tipo de integração
          </Typography>

          {/* Mesmo padrão da seleção de tipos em Canais (flex wrap) */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {INTEGRATION_CARDS.map((card) => (
              <Box
                key={card.key}
                sx={{
                  flex: "1 1 280px",
                  minWidth: 280,
                  maxWidth: {
                    xs: "100%",
                    sm: "calc(50% - 24px)",
                    md: "calc(33.33% - 24px)",
                    lg: "calc(25% - 24px)",
                  },
                  mb: 5,
                  boxSizing: "border-box",
                }}
              >
                <TypeCard
                  card={card}
                  selected={card.key === typeKey}
                  onClick={() => setTypeKey(card.key)}
                />
              </Box>
            ))}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => setStep(2)}
              disabled={!typeKey || isLoading}
            >
              Continuar
            </Button>
          </Stack>
        </>
      ) : (
        <form onSubmit={submit}>
          {!isEditing && (
            <Box sx={{ mb: 2 }}>
              <Button
                size="small"
                variant="text"
                onClick={() => setStep(1)}
                disabled={isLoading}
              >
                Trocar tipo
              </Button>
            </Box>
          )}

          {isPassagensType ? (
            <>
              {passagensMessage && (
                <Alert severity={passagensMessage.type} sx={{ mb: 2 }}>
                  {passagensMessage.text}
                </Alert>
              )}

              {passagensTypeCard?.icon && (
                <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                  <Box sx={{ color: "primary.main", display: "flex", pt: 0.25 }}>
                    {React.createElement(passagensTypeCard.icon)}
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {passagensTypeCard.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {passagensTypeCard.description}
                    </Typography>
                  </Box>
                </Stack>
              )}

              <Box>
                <FormControlLabel
                  sx={{ mb: 1 }}
                  control={
                    <Switch
                      checked={!!passagensForm.sync_partner_default}
                      disabled={passagensLoading || passagensSaving}
                      onChange={(e) =>
                        setPassagensForm((p) => ({ ...p, sync_partner_default: e.target.checked }))
                      }
                    />
                  }
                  label="Definir este parceiro como padrão para consulta de passagens neste workspace"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  As credenciais são salvas nas mesmas chaves usadas pelo Xpro legado e nos atalhos
                  passagens_*, para não quebrar integrações existentes.
                </Typography>
                <Grid container spacing={2}>
                  {(passagensFieldSplit.primary || []).map((f) => (
                    <Grid item xs={12} md={6} key={f.key}>
                      <TextField
                        fullWidth
                        type={f.password ? "password" : "text"}
                        label={f.label}
                        value={passagensForm[f.key] ?? ""}
                        disabled={passagensLoading || passagensSaving}
                        onChange={(e) =>
                          setPassagensForm((p) => ({ ...p, [f.key]: e.target.value }))
                        }
                      />
                    </Grid>
                  ))}
                  {passagensPartner === "praxxio" && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!passagensForm.praxxio_bpe}
                            disabled={passagensLoading || passagensSaving}
                            onChange={(e) =>
                              setPassagensForm((p) => ({ ...p, praxxio_bpe: e.target.checked }))
                            }
                          />
                        }
                        label="Emitir BPe na Praxio (se desligado, emite voucher / TFO)"
                      />
                    </Grid>
                  )}
                  {passagensFieldSplit.hasAdvanced && (
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => setPassagensShowAdvanced((v) => !v)}
                        disabled={passagensLoading || passagensSaving}
                        sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                      >
                        {passagensShowAdvanced ? "Ocultar opções avançadas" : "Mais opções (avançado)"}
                      </Button>
                      <Collapse in={passagensShowAdvanced}>
                        <Grid container spacing={2} sx={{ mt: 0.5 }}>
                          {(passagensFieldSplit.advanced || []).map((f) => (
                            <Grid item xs={12} md={6} key={f.key}>
                              <TextField
                                fullWidth
                                type={f.password ? "password" : "text"}
                                label={f.label}
                                value={passagensForm[f.key] ?? ""}
                                disabled={passagensLoading || passagensSaving}
                                onChange={(e) =>
                                  setPassagensForm((p) => ({ ...p, [f.key]: e.target.value }))
                                }
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Collapse>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        onClick={() => runPassagensTest(passagensPartner)}
                        disabled={!!passagensTesting || passagensLoading || !passagensPartner}
                      >
                        {passagensTesting === passagensPartner
                          ? `Testando ${passagensTestUiLabel}...`
                          : `Testar conexão ${passagensTestUiLabel}`}
                      </Button>
                      {statusChip(passagensPartner)}
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3 }}>
                <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  onClick={savePassagensConfig}
                  disabled={passagensLoading || passagensSaving}
                >
                  {passagensSaving ? "Salvando..." : "Salvar"}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Nome"
                    name="nome"
                    value={form.nome}
                    onChange={handleChange}
                    fullWidth
                    required
                  />
                </Grid>
                {visible.authType && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label="Tipo de Autenticação"
                      name="tipo_auth"
                      value={form.tipo_auth}
                      onChange={handleChange}
                      fullWidth
                    >
                      {AUTH_TYPES.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                )}

          {visible.provider && (
            <Grid item xs={12} md={6}>
              <TextField label="Provedor" name="provedor" value={form.provedor} onChange={handleChange} fullWidth />
            </Grid>
          )}
          {visible.baseUrl && (
            <Grid item xs={12} md={6}>
              <TextField label="Base URL" name="base_url" value={form.base_url} onChange={handleChange} fullWidth />
            </Grid>
          )}

          {visible.order && (
            <Grid item xs={12} md={4}>
              <TextField
                label="Ordem"
                name="ordem"
                value={form.ordem ?? ""}
                onChange={(e) => setForm((p) => ({ ...p, ordem: digitsOnly(e.target.value) }))}
                inputMode="numeric"
                fullWidth
              />
            </Grid>
          )}
          {visible.status && (
            <Grid item xs={12} md={visible.order ? 8 : 12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Number(form.status) === 1}
                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.checked ? 1 : 0 }))}
                  />
                }
                label={Number(form.status) === 1 ? "Ativa" : "Inativa"}
              />
            </Grid>
          )}

          {/* Credenciais dinâmicas */}
          {visible.credentials && showApiKey && (
            <Grid item xs={12} md={6}>
              {(() => {
                const hasStored = form.has_api_key;
                const isMasked = hasStored && !form.api_key;
                return (
              <TextField
                label="API Key"
                name="api_key"
                    value={isMasked ? "••••••••" : form.api_key}
                    onChange={isMasked ? undefined : handleChange}
                    type="password"
                    autoComplete="new-password"
                fullWidth
                helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !form.api_key && form.has_api_key
                    ? "Já existe uma API Key configurada. Preencha apenas se quiser trocar."
                    : undefined
                }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("api_key") : undefined}
                    onFocus={isMasked ? () => maskSecretField("api_key") : undefined}
              />
                );
              })()}
            </Grid>
          )}
          {visible.credentials && showOAuth && (
            <>
              <Grid item xs={12} md={6}>
                <TextField label="Client ID" name="client_id" value={form.client_id} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                {(() => {
                  const hasStored = form.has_client_secret;
                  const isMasked = hasStored && !form.client_secret;
                  return (
                <TextField
                  label="Client Secret"
                  name="client_secret"
                      value={isMasked ? "••••••••" : form.client_secret}
                      onChange={isMasked ? undefined : handleChange}
                      type="password"
                      autoComplete="new-password"
                  fullWidth
                  helperText={
                        isMasked
                          ? "Valor já configurado. Clique para alterar."
                          : !form.client_secret && form.has_client_secret
                      ? "Já existe um Client Secret configurado. Preencha apenas se quiser trocar."
                      : undefined
                  }
                      InputProps={isMasked ? { readOnly: true } : undefined}
                      onClick={isMasked ? () => maskSecretField("client_secret") : undefined}
                      onFocus={isMasked ? () => maskSecretField("client_secret") : undefined}
                />
                  );
                })()}
              </Grid>
            </>
          )}
          {visible.credentials && showBasic && (
            <>
              <Grid item xs={12} md={6}>
                <TextField label="Username" name="username" value={form.username} onChange={handleChange} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                {(() => {
                  const hasStored = form.has_password;
                  const isMasked = hasStored && !form.password;
                  return (
                <TextField
                  label="Password"
                  name="password"
                      value={isMasked ? "••••••••" : form.password}
                      onChange={isMasked ? undefined : handleChange}
                      type="password"
                      autoComplete="new-password"
                  fullWidth
                  helperText={
                        isMasked
                          ? "Valor já configurado. Clique para alterar."
                          : !form.password && form.has_password
                      ? "Já existe uma senha configurada. Preencha apenas se quiser trocar."
                      : undefined
                  }
                      InputProps={isMasked ? { readOnly: true } : undefined}
                      onClick={isMasked ? () => maskSecretField("password") : undefined}
                      onFocus={isMasked ? () => maskSecretField("password") : undefined}
                />
                  );
                })()}
              </Grid>
            </>
          )}

          {/* Webhook */}
          {visible.webhook && (
            <>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Webhook URL (callback)"
                  name="webhook_url"
                  value={form.webhook_url}
                  onChange={handleChange}
                  fullWidth
                  placeholder="https://dominio.xpro.me/integrations/webhook/{id}"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                {(() => {
                  const hasStored = form.has_webhook_secret;
                  const isMasked = hasStored && !form.webhook_secret;
                  return (
                <TextField
                  label="Webhook Secret"
                  name="webhook_secret"
                    value={isMasked ? "••••••••" : form.webhook_secret}
                    onChange={isMasked ? undefined : handleChange}
                    type="password"
                    autoComplete="new-password"
                  fullWidth
                  helperText={
                      isMasked
                        ? "Valor já configurado. Clique para alterar."
                        : !form.webhook_secret && form.has_webhook_secret
                      ? "Já existe um Webhook Secret configurado. Preencha apenas se quiser trocar."
                      : undefined
                  }
                    InputProps={isMasked ? { readOnly: true } : undefined}
                    onClick={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                    onFocus={isMasked ? () => maskSecretField("webhook_secret") : undefined}
                />
                  );
                })()}
              </Grid>
            </>
          )}

          {visible.scopes && (
            <Grid item xs={12}>
              <TextField
                label="Scopes (separados por vírgula)"
                name="scopes"
                value={form.scopes}
                onChange={handleChange}
                fullWidth
                placeholder="read,write,offlinescope"
              />
            </Grid>
          )}

          {visible.observations && (
            <Grid item xs={12}>
              <TextField
                label="Observações"
                name="observacoes"
                value={form.observacoes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
              />
            </Grid>
          )}
              </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={onClose} disabled={isLoading}>Cancelar</Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              {visible.testButton && (
                <Button variant="outlined" onClick={testNow} disabled={isLoading}>
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : "Testar conexão"}
                </Button>
              )}
              {visible.saveButton && (
                <Button type="submit" variant="contained" disabled={isLoading}>
                  {isLoading ? <CircularProgress size={20} color="inherit" /> : "Salvar"}
                </Button>
              )}
            </Box>
          </Box>
            </>
          )}
        </form>
      )}
    </Paper>
  );
};

export default AddIntegrationForm;
